import { ReactElement, useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { FormContainer, FormTabs, useFetch } from "@4uhub/lib4uhub";

import { useTranslation } from "react-i18next";
import { ModalForm } from "../../../../../components/ModalForm/ModalForm";
import {
  ISalesContract,
  ISalesContractPayload,
} from "../../../../../models/sales-contract";
import {
  createSalesContract,
  findSalesContractById,
  updateSalesContract,
} from "../../../../../services/salesContract.service";
import { SalesContractProvider } from "../../../../../store/contexts/SalesContractContext";
import { ContractualObjects } from "../../ContractualObjects/ContractualObjects";
import { SalesContractAttachmentsPage } from "../../SalesContractAttachments/SalesContractAttachmentsPage";
import { SalesContractBeneficiarysPage } from "../../SalesContractBeneficiary/SalesContractBeneficiaryPage";
import { SalesContractHistoriesPage } from "../../SalesContractHistories/SalesContractHistoriesPage";
import { SalesContractPayerPage } from "../../SalesContractPayer/SalesContractPayerPage";
import {
  advantagesAndBenefitsClubSchema,
  type TAdvantagesAndBenefitsClubForm,
} from "../advantage-and-benefits-club.schema";
import { Fields } from "./Fields/Fields";
import { RenderForms } from "./RenderForms/RenderForms";
import { SalesContractRules } from "../../SalesContractRule/SalesContractRules";
const translationsPath =
  "page.4uSalesContract.registers.baseContracts.advantagesAndBenefitsClub.";

const AdvantagesAndBenefitsClub = (): ReactElement => {
  const { id, beneficiaryId } = useParams();

  const [dataSalesContract, setDataSalesContract] = useState<ISalesContract>();

  const { sendRequest: get, loading: isFetching } = useFetch(
    findSalesContractById
  );
  const { t } = useTranslation();
  const { sendRequest: create, loading: isCreating } =
    useFetch(createSalesContract);
  const { sendRequest: update, loading: isUpdating } =
    useFetch(updateSalesContract);

  const handlePayload = useCallback(
    ({
      situation,
      mainContract,
      salesGrantor,
      salesContractType,
      individualStipulant,
      legalEntityStipulant,
      salesContractClassification,
      salesContractTerminationReason,
      legalEntityBenefitsAdministrator,
      ...rest
    }: TAdvantagesAndBenefitsClubForm) => {
      const payload: ISalesContractPayload = {
        ...rest,
        situationId: situation?.id,
        mainContractId: mainContract?.id,
        salesGrantorId: salesGrantor?.id,
        legalEntityBenefitsAdministratorId:
          legalEntityBenefitsAdministrator?.id,
        salesContractTypeId: salesContractType?.id,
        individualStipulantId: individualStipulant?.id,
        legalEntityStipulantId: legalEntityStipulant?.id,
        salesContractClassificationId: salesContractClassification?.id,
        salesContractTerminationReasonId: salesContractTerminationReason?.id,
      };

      return payload;
    },
    []
  );

  const handlePerson = (data: any) => {
    if (data) {
      return {
        ...data,
        id: data?.identifier,
      };
    }

    return null;
  };

  const updateHandler = useCallback(
    (updates: TAdvantagesAndBenefitsClubForm, id: string) =>
      update({ id, item: handlePayload(updates) } as {
        id: string;
        item: ISalesContractPayload;
      }),
    [update, handlePayload]
  );

  const createHandler = useCallback(
    (values: TAdvantagesAndBenefitsClubForm) => create(handlePayload(values)),
    [create, handlePayload]
  );

  const getHandler = useCallback(
    async (id: string) => {
      const { data, success } = await get(id);

      if (data && success) {
        setDataSalesContract(data);
        let entity: "QPF" | "QPJ" = "QPF";

        if (data?.legalEntityStipulant) {
          entity = "QPJ";
        }

        const response = {
          ...data,
          entity,
          contractDate: data.contractDate
            ? new Date(`${data.contractDate}T00:00:00`)
            : null,
          renewalPeriodMonths: data?.renewalPeriodMonths?.toString?.(),
          salesContractType: data?.salecContractType,
          legalEntityBenefitsAdministrator: data.salesContractClubAb
            ? data.salesContractClubAb.legalEntityBenefitsAdministrator
            : null,
          mainContract: data?.mainContract
            ? {
                ...data?.mainContract,
                name: data?.mainContract?.contractTitle,
              }
            : null,
          individualStipulant: handlePerson(data?.individualStipulant),
          legalEntityStipulant: data?.legalEntityStipulant,
        };

        return { data: response, success };
      }

      return { data, success } as any;
    },
    [get]
  );

  const isLoading = isFetching || isCreating || isUpdating;

  return (
    <SalesContractProvider>
      <ModalForm>
        <RenderForms />
      </ModalForm>
      <FormContainer<TAdvantagesAndBenefitsClubForm>
        title={t(translationsPath + "title")}
        subtitle=""
        isModalForm
        propId={id}
        loading={isLoading}
        schema={advantagesAndBenefitsClubSchema}
        getFunction={getHandler}
        saveFunction={createHandler}
        updateFunction={updateHandler}
        fields={(type) => (
          <FormTabs
            defaultTab={beneficiaryId ? 5 : 0}
            tabs={[
              {
                label: t(translationsPath + "tabs.contract"),
                component: (
                  <Fields type={type} dataSalesContract={dataSalesContract} />
                ),
              },
              {
                label: t(translationsPath + "tabs.contractual_object"),
                component: <ContractualObjects />,
                disabled: !id,
              },
              {
                label: t(translationsPath + "tabs.sales_contract_rules"),
                component: <SalesContractRules />,
                disabled: !id,
              },
              {
                label: t(translationsPath + "tabs.payer"),
                component: <SalesContractPayerPage />,
                disabled: !id,
              },
              {
                label: t(translationsPath + "tabs.history"),
                component: <SalesContractHistoriesPage />,
                disabled: !id,
              },
              {
                label: t(translationsPath + "tabs.attachment"),
                component: <SalesContractAttachmentsPage />,
                disabled: !id,
              },
              {
                label: t(translationsPath + "tabs.beneficiary"),
                component: <SalesContractBeneficiarysPage />,
                disabled: !id,
              },
            ]}
          />
        )}
      />
    </SalesContractProvider>
  );
};

export default AdvantagesAndBenefitsClub;
